import { useState } from "react";
import { Link } from "react-router-dom";
import { Store, Loader2, MapPin, MailCheck } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-extrabold text-lg font-outfit">S</div>
            <div>
              <div className="font-extrabold font-outfit text-lg leading-tight">Sonu Ki Dukaan</div>
              <div className="text-[11px] text-muted-foreground font-mono">Admin Panel</div>
            </div>
          </div>

          {sent ? (
            <div data-testid="forgot-sent-message">
              <MailCheck className="h-10 w-10 text-primary mb-4" />
              <h1 className="text-2xl font-extrabold font-outfit tracking-tight">Check your email</h1>
              <p className="text-sm text-muted-foreground mt-2">
                If that email belongs to the admin account, a password reset link has been sent. The link expires in 1 hour.
              </p>
              <Link to="/login" className="inline-block mt-6 text-sm font-medium text-primary hover:underline" data-testid="back-to-login-link">← Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-password-form">
              <h1 className="text-2xl font-extrabold font-outfit tracking-tight">Forgot password?</h1>
              <p className="text-sm text-muted-foreground mt-1 mb-6">Enter your admin email and we’ll send a reset link.</p>
              {error && (
                <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="forgot-error">{error}</div>
              )}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Admin email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" autoComplete="email" required className="mt-1.5" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" className="w-full" disabled={loading} data-testid="forgot-submit-button">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : "Send reset link"}
                </Button>
                <Link to="/login" className="block text-center text-sm font-medium text-primary hover:underline" data-testid="back-to-login-link">← Back to sign in</Link>
              </div>
            </form>
          )}
        </div>
      </div>
      <div className="hidden lg:flex flex-col justify-between bg-primary text-primary-foreground p-10 relative overflow-hidden">
        <div className="relative z-10">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-white/15">
            <MapPin className="h-3 w-3" /> Operating in Lucknow, UP
          </span>
        </div>
        <div className="relative z-10">
          <Store className="h-10 w-10 mb-4 opacity-90" />
          <h2 className="text-3xl font-extrabold font-outfit leading-tight">Bachat Ki Dukaan</h2>
          <p className="mt-3 text-sm text-primary-foreground/80 max-w-md">Secure admin access to your hyperlocal marketplace control center.</p>
        </div>
        <div className="absolute -right-20 -bottom-20 h-80 w-80 rounded-full bg-white/10" />
      </div>
    </div>
  );
}
